import React, { useEffect, useState } from 'react'
import { Spinner as ButtonSpinner } from 'reactstrap'
import './MyProfileModal.scss'
import {
  convertDMY,
  getCvLinkHref,
  hasPermission,
  parseCvLinks
} from '../../../../utility/Utils'
import { baseUrl } from '../../../../app.config'
import { IS_ADMIN } from '../../../../utility/constants'
import { formatRoles, initialsOf } from '../../../../layouts/ntts/navModel'
import CustomModal from '../../../../utility/commonComponents/CustomModal/CustomModal'
import Spinner from '@components/spinner/Loading-spinner'
import {
  Edit2,
  Plus,
  Trash2,
  Check,
  X,
  Mail,
  Phone,
  Calendar,
  Cpu,
  Link2
} from 'react-feather'
import toast from 'react-hot-toast'

const cvLinkRegex = /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i

const MyProfileModal = ({ isOpen, close, setShow }) => {
  const [userData, setUserData] = useState({})
  const [loading, setLoading] = useState(true)
  const [isEditingCv, setIsEditingCv] = useState(false)
  const [cvList, setCvList] = useState([])
  const [isSavingCv, setIsSavingCv] = useState(false)

  const fetchData = async () => {
    try {
      const response = await axios.get(`${baseUrl()}/user/userProfile`)
      setUserData(response?.data?.data || {})
      setLoading(false)
    } catch (error) {
      console.error('Error fetching profile data:', error)
      setLoading(false)
    }
  }

  const handleModalClosed = () => {
    setIsEditingCv(false)
    setShow(false)
  }

  const handleStartEditCv = () => {
    const parsed = parseCvLinks(userData?.cvLinks)
    setCvList(parsed.length ? parsed : [''])
    setIsEditingCv(true)
  }

  const handleCancelCv = () => {
    setIsEditingCv(false)
  }

  const handleCvChange = (index, value) => {
    setCvList((prev) => {
      const updated = [...prev]
      updated[index] = value
      return updated
    })
  }

  const handleAddCv = () => {
    setCvList((prev) => [...prev, ''])
  }

  const handleRemoveCv = (index) => {
    setCvList((prev) => {
      const updated = prev.filter((_, i) => i !== index)
      return updated.length ? updated : ['']
    })
  }

  const handleSaveCv = async () => {
    const trimmed = cvList
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean)

    for (const link of trimmed) {
      if (!cvLinkRegex.test(link)) {
        toast.error(`Invalid URL: ${link}`)
        return
      }
    }

    try {
      setIsSavingCv(true)
      const res = await axios.put(baseUrl() + '/user/me', trimmed)
      toast.success(res?.data?.message || 'CV links updated successfully')
      await fetchData()
      setIsEditingCv(false)
    } catch (error) {
      console.error('Error updating CV links:', error)
      toast.error(error?.response?.data?.message || 'Failed to update CV links')
    } finally {
      setIsSavingCv(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen])

  // Local storage fallback for role / name if not returned in profile response
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('userData')) || {}
    } catch {
      return {}
    }
  })()

  const name = userData?.name || storedUser?.name || 'User'
  const role = formatRoles(userData?.role || storedUser?.role)

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleModalClosed}
      title="My Profile"
      className="ntts-profile-dialog"
    >
      <div className="ntts-dialog__body">
        {loading ? (
          <div className="ntts-profile__loading">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Profile Avatar & Identity */}
            <div className="ntts-profile__head">
              <div className="ntts-profile__avatar">
                {initialsOf(name)}
              </div>
              <h3 className="ntts-profile__name">{name}</h3>
              {role && <span className="ntts-profile__role">{role}</span>}
            </div>

            {/* Profile Info List */}
            <div className="ntts-profile__info-list">
              {/* Email */}
              <div className="ntts-profile__info-item">
                <div className="ntts-profile__info-icon">
                  <Mail />
                </div>
                <div className="ntts-profile__info-content">
                  <span className="ntts-profile__info-label">Email</span>
                  <span className="ntts-profile__info-value">
                    {userData?.email || '--'}
                  </span>
                </div>
              </div>

              {/* Contact No */}
              <div className="ntts-profile__info-item">
                <div className="ntts-profile__info-icon">
                  <Phone />
                </div>
                <div className="ntts-profile__info-content">
                  <span className="ntts-profile__info-label">Contact No</span>
                  <span className="ntts-profile__info-value">
                    {userData?.phoneNumber
                      ? `+91 ${userData.phoneNumber}`
                      : '--'}
                  </span>
                </div>
              </div>

              {/* Technologies (non-admin only) */}
              {!hasPermission(IS_ADMIN) && (
                <div className="ntts-profile__info-item">
                  <div className="ntts-profile__info-icon">
                    <Cpu />
                  </div>
                  <div className="ntts-profile__info-content">
                    <span className="ntts-profile__info-label">Technologies</span>
                    {userData?.technologyName ? (
                      <div className="ntts-profile__tags">
                        {userData.technologyName
                          .split(',')
                          .map((value) => value.trim())
                          .filter(Boolean)
                          .map((value, index) => (
                            <span key={index} className="ntts-profile__tag">
                              {value}
                            </span>
                          ))}
                      </div>
                    ) : (
                      <span className="ntts-profile__info-value">--</span>
                    )}
                  </div>
                </div>
              )}

              {/* CV Links (non-admin only) */}
              {!hasPermission(IS_ADMIN) && (
                <div className="ntts-profile__info-item">
                  <div className="ntts-profile__info-icon">
                    <Link2 />
                  </div>
                  <div className="ntts-profile__info-content">
                    <div className="ntts-profile__info-head">
                      <span className="ntts-profile__info-label">CV Links</span>
                      {!isEditingCv && (
                        <button
                          type="button"
                          className="ntts-icon-btn ntts-profile__edit"
                          onClick={handleStartEditCv}
                          title="Edit CV Links"
                          aria-label="Edit CV Links"
                        >
                          <Edit2 />
                        </button>
                      )}
                    </div>

                    {!isEditingCv ? (
                      parseCvLinks(userData?.cvLinks).length ? (
                        <div className="ntts-profile__links">
                          {parseCvLinks(userData?.cvLinks).map((link, index) => (
                            <a
                              key={index}
                              href={getCvLinkHref(link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ntts-profile__link"
                            >
                              {link}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="ntts-profile__info-value">--</span>
                      )
                    ) : (
                      <div className="ntts-profile__cv-edit">
                        {cvList.map((link, index) => (
                          <div key={index} className="ntts-profile__cv-row">
                            <input
                              type="text"
                              className="ntts-input"
                              placeholder="https://drive.google.com/..."
                              value={link}
                              onChange={(e) =>
                                handleCvChange(index, e.target.value)
                              }
                              disabled={isSavingCv}
                            />
                            <button
                              type="button"
                              className="ntts-icon-btn ntts-profile__remove"
                              onClick={() => handleRemoveCv(index)}
                              title="Remove"
                              aria-label="Remove link"
                              disabled={isSavingCv}
                            >
                              <Trash2 />
                            </button>
                          </div>
                        ))}

                        <div className="ntts-profile__cv-actions">
                          <button
                            type="button"
                            className="ntts-btn ntts-btn--sm"
                            onClick={handleAddCv}
                            disabled={isSavingCv}
                          >
                            <Plus /> Add Link
                          </button>
                          <div className="ntts-profile__cv-buttons">
                            <button
                              type="button"
                              className="ntts-btn ntts-btn--sm"
                              onClick={handleCancelCv}
                              disabled={isSavingCv}
                            >
                              <X />
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="ntts-btn ntts-btn--sm ntts-btn--primary"
                              onClick={handleSaveCv}
                              disabled={isSavingCv}
                            >
                              {isSavingCv ? (
                                <>
                                  <ButtonSpinner size="sm" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check />
                                  Save
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Date of Joining */}
              <div className="ntts-profile__info-item">
                <div className="ntts-profile__info-icon">
                  <Calendar />
                </div>
                <div className="ntts-profile__info-content">
                  <span className="ntts-profile__info-label">
                    Date of Joining
                  </span>
                  <span className="ntts-profile__info-value">
                    {userData?.dateOfJoining
                      ? convertDMY(userData.dateOfJoining)
                      : '--'}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="ntts-dialog__footer">
        <button
          type="button"
          className="ntts-btn ntts-btn--primary"
          onClick={handleModalClosed}
        >
          Close
        </button>
      </div>
    </CustomModal>
  )
}

export default MyProfileModal
